import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ExternalLink, Copy, Save, Users, Calendar, CheckCircle, XCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const Settings = () => {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState({
    connected: false,
    email: null,
    configured: false,
    loading: true
  });
  const [connectingGcal, setConnectingGcal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    email: "",
    phone: "",
    address: "",
    timezone: "Europe/Rome",
    logo_url: "",
    slug: "",
    total_resources: 1
  });

  useEffect(() => {
    fetchData();
    fetchGoogleCalendarStatus();
    
    // Check if we just connected Google Calendar
    if (searchParams.get('gcal') === 'connected') {
      toast.success("Google Calendar collegato con successo!");
      // Remove the query param from URL
      window.history.replaceState({}, '', '/settings');
    }
  }, []);

  const fetchGoogleCalendarStatus = async () => {
    try {
      const response = await axios.get(`${API}/google-calendar/status`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setGoogleCalendarStatus({
        ...response.data,
        loading: false
      });
    } catch (error) {
      setGoogleCalendarStatus({
        connected: false,
        email: null,
        configured: false,
        loading: false
      });
    }
  };

  const connectGoogleCalendar = async () => {
    setConnectingGcal(true);
    try {
      const response = await axios.get(`${API}/google-calendar/auth-url`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossibile avviare la connessione a Google Calendar");
      setConnectingGcal(false);
    }
  };

  const disconnectGoogleCalendar = async () => {
    try {
      await axios.post(`${API}/google-calendar/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Google Calendar disconnesso");
      fetchGoogleCalendarStatus();
    } catch (error) {
      toast.error("Errore durante la disconnessione");
    }
  };

  const fetchData = async () => {
    try {
      const bizResponse = await axios.get(`${API}/businesses`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      if (bizResponse.data.length > 0) {
        const biz = bizResponse.data[0];
        setBusiness(biz);
        setFormData({
          name: biz.name || "",
          description: biz.description || "",
          email: biz.email || "",
          phone: biz.phone || "",
          address: biz.address || "",
          timezone: biz.timezone || "Europe/Rome",
          logo_url: biz.logo_url || "",
          slug: biz.slug || "",
          total_resources: biz.total_resources || 1
        });
      }
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/businesses/${business.business_id}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Impostazioni salvate!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const copyBookingLink = () => {
    const link = `${window.location.origin}/book/${business?.slug}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copiato negli appunti!");
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!business) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Crea prima la tua attività dalla Dashboard</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Impostazioni
          </h1>
          <p className="text-slate-500 mt-1">Gestisci le informazioni della tua attività</p>
        </div>

        {/* Booking Link */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Link Prenotazioni</CardTitle>
            <CardDescription>Condividi questo link con i tuoi clienti</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <Input 
                value={`${window.location.origin}/book/${business.slug}`}
                readOnly
                className="font-mono text-sm"
                data-testid="booking-link-input"
              />
              <Button variant="outline" size="icon" onClick={copyBookingLink} data-testid="copy-link-btn">
                <Copy className="h-4 w-4" />
              </Button>
              <a href={`/book/${business.slug}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="icon" data-testid="open-link-btn">
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Business Settings Form */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Informazioni Attività</CardTitle>
            <CardDescription>Aggiorna i dettagli della tua attività</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome Attività *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="settings-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrizione</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="settings-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="settings-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefono</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="settings-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Indirizzo</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  data-testid="settings-address-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="logo_url">URL Logo</Label>
                <Input
                  id="logo_url"
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  placeholder="https://..."
                  data-testid="settings-logo-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">URL Personalizzato</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">{window.location.origin}/book/</span>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') })}
                    className="flex-1"
                    data-testid="settings-slug-input"
                  />
                </div>
              </div>

              <div className="pt-4">
                <Button type="submit" disabled={saving} data-testid="save-settings-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? "Salvataggio..." : "Salva Modifiche"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Capacity Settings */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Capacità / Risorse
              </div>
            </CardTitle>
            <CardDescription>
              Configura il numero di posti o risorse disponibili contemporaneamente (es. sedie in un salone, sale riunioni, postazioni)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="total_resources">Numero di Posti / Risorse</Label>
                <Input
                  id="total_resources"
                  type="number"
                  min="1"
                  max="100"
                  value={formData.total_resources}
                  onChange={(e) => setFormData({ ...formData, total_resources: parseInt(e.target.value) || 1 })}
                  className="max-w-[200px]"
                  data-testid="settings-resources-input"
                />
                <p className="text-sm text-slate-500">
                  Questo valore determina quante prenotazioni possono essere accettate nello stesso orario.
                  Ad esempio, se hai un salone con 3 sedie, imposta 3 per permettere fino a 3 appuntamenti contemporanei.
                </p>
              </div>

              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-sm mb-2">Come funziona:</h4>
                <ul className="text-sm text-slate-600 space-y-1 list-disc ml-4">
                  <li>Se hai <strong>{formData.total_resources}</strong> posti e alle 10:00 ci sono già {formData.total_resources} prenotazioni, il {formData.total_resources + 1}° cliente vedrà lo slot come non disponibile</li>
                  <li>Gli slot mostrati ai clienti indicheranno quanti posti sono ancora liberi</li>
                  <li>Questo permette di gestire attività come saloni, centri benessere, palestre, sale riunioni</li>
                </ul>
              </div>

              <div className="pt-2">
                <Button type="submit" disabled={saving} data-testid="save-capacity-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? "Salvataggio..." : "Salva Capacità"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Google Calendar Integration */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Google Calendar
              </div>
            </CardTitle>
            <CardDescription>
              Collega il tuo Google Calendar per sincronizzare automaticamente le prenotazioni
            </CardDescription>
          </CardHeader>
          <CardContent>
            {googleCalendarStatus.loading ? (
              <div className="flex items-center gap-2 text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Verifica connessione...</span>
              </div>
            ) : googleCalendarStatus.connected ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  <div>
                    <p className="font-medium text-emerald-900">Calendario collegato</p>
                    <p className="text-sm text-emerald-700">{googleCalendarStatus.email}</p>
                  </div>
                </div>
                
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-medium text-sm mb-2">Funzionalità attive:</h4>
                  <ul className="text-sm text-slate-600 space-y-1 list-disc ml-4">
                    <li>Le nuove prenotazioni confermate verranno aggiunte automaticamente al tuo calendario</li>
                    <li>Riceverai promemoria via email e notifiche dal calendario</li>
                    <li>I clienti vedranno le informazioni della prenotazione nell'evento</li>
                  </ul>
                </div>

                <Button 
                  variant="outline" 
                  onClick={disconnectGoogleCalendar}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="disconnect-gcal-btn"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Scollega Google Calendar
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {googleCalendarStatus.configured ? (
                  <>
                    <p className="text-slate-600">
                      Collega il tuo Google Calendar per avere le prenotazioni automaticamente sincronizzate.
                    </p>
                    
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="font-medium text-sm mb-2">Cosa succederà:</h4>
                      <ul className="text-sm text-slate-600 space-y-1 list-disc ml-4">
                        <li>Ogni prenotazione confermata creerà un evento nel tuo calendario</li>
                        <li>L'evento includerà i dettagli del cliente e dei servizi prenotati</li>
                        <li>Riceverai promemoria automatici prima di ogni appuntamento</li>
                      </ul>
                    </div>

                    <Button 
                      onClick={connectGoogleCalendar}
                      disabled={connectingGcal}
                      data-testid="connect-gcal-btn"
                    >
                      {connectingGcal ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Connessione...
                        </>
                      ) : (
                        <>
                          <Calendar className="h-4 w-4 mr-2" />
                          Collega Google Calendar
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg">
                    <XCircle className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-900">Integrazione non configurata</p>
                      <p className="text-sm text-amber-700">
                        L'integrazione con Google Calendar richiede la configurazione delle credenziali API. 
                        Contatta l'amministratore per abilitare questa funzionalità.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
